import React, { useCallback } from "react";
import { MdEmojiEvents } from "react-icons/md";
import { Challenge } from "../../types/challenge";
import { PaginatedResult } from "../../types/api";
import { ChallengeService } from "../../services/challengeService";
import { useAssignmentLists } from "../../hooks/useAssignmentLists";
import { useAssignmentModalState } from "../../hooks/useAssignmentModalState";
import { pageFrom } from "../../hooks/usePaginatedResource";
import AssignmentModalLayout from "../modals/AssignmentModalLayout";
import AssignmentPanel from "../modals/AssignmentPanel";
import AssignmentItemCard from "../modals/AssignmentItemCard";

interface ChallengeAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  programId: number;
  programName: string;
  assignedChallenges: Challenge[];
  onUpdate: () => void;
}

const ChallengeAssignmentModal: React.FC<ChallengeAssignmentModalProps> = ({
  isOpen,
  onClose,
  programId,
  programName,
  assignedChallenges,
  onUpdate,
}) => {
  // The assigned side comes from the program already loaded by the page
  const loadAssigned = useCallback(
    async () => assignedChallenges,
    [assignedChallenges]
  );

  // Challenges are few, so the whole set arrives as a single page
  const loadAvailablePage =
    useCallback(async (): Promise<PaginatedResult<Challenge> | null> => {
      const challenges = await ChallengeService.getChallenges();
      if (!challenges) return null;
      return pageFrom(challenges, {
        total: challenges.length,
        page: 1,
        limit: challenges.length,
      });
    }, []);

  const lists = useAssignmentLists<Challenge>({
    isOpen,
    loadAssigned,
    loadAvailablePage,
    errorMessage: "Failed to load challenges. Please try again.",
  });

  const modalState = useAssignmentModalState({
    isOpen,
    onClose,
    onUpdate,
    loadError: lists.error,
  });

  const handleAssign = (challenge: Challenge) =>
    modalState.runAction({
      kind: "assign",
      id: challenge.id,
      request: () =>
        ChallengeService.assignProgramToChallenge(challenge.id, programId),
      onSuccess: () => lists.markAssigned(challenge),
      successMessage: `${challenge.name} assigned successfully`,
      failureMessage: "Failed to assign challenge",
    });

  const handleUnassign = (challenge: Challenge) =>
    modalState.runAction({
      kind: "unassign",
      id: challenge.id,
      request: () =>
        ChallengeService.unassignProgramFromChallenge(challenge.id, programId),
      onSuccess: () => lists.markUnassigned(challenge),
      successMessage: `${challenge.name} unassigned successfully`,
      failureMessage: "Failed to unassign challenge",
    });

  return (
    <AssignmentModalLayout
      isOpen={isOpen}
      onClose={modalState.handleClose}
      title="Manage Challenges"
      subtitle={programName}
      message={modalState.message}
    >
      <div className="flex flex-col overflow-hidden md:flex-row">
        <AssignmentPanel
          title="Available Challenges"
          countLabel={`${lists.availableTotal} challenges`}
          tone="available"
          loading={lists.availableLoading}
          isEmpty={lists.available.length === 0}
          emptyIcon={MdEmojiEvents}
          emptyTitle="All challenges are assigned"
        >
          {lists.available.map((challenge) => (
            <AssignmentItemCard
              key={`available-${challenge.id}`}
              id={challenge.id}
              title={challenge.name}
              subtitle={String(challenge.conducted_at_year)}
              isAssigned={false}
              onToggle={() => handleAssign(challenge)}
              isToggling={modalState.assigningItems.has(challenge.id)}
              showImage={false}
            />
          ))}
        </AssignmentPanel>

        <AssignmentPanel
          title="Assigned To Program"
          countLabel={`${lists.assigned.length} challenges`}
          tone="assigned"
          loading={lists.assignedLoading}
          isEmpty={lists.assigned.length === 0}
          emptyIcon={MdEmojiEvents}
          emptyTitle="No challenges assigned yet"
        >
          {lists.assigned.map((challenge) => (
            <AssignmentItemCard
              key={`assigned-${challenge.id}`}
              id={challenge.id}
              title={challenge.name}
              subtitle={String(challenge.conducted_at_year)}
              isAssigned={true}
              onToggle={() => handleUnassign(challenge)}
              isToggling={modalState.unassigningItems.has(challenge.id)}
              showImage={false}
            />
          ))}
        </AssignmentPanel>
      </div>
    </AssignmentModalLayout>
  );
};

export default ChallengeAssignmentModal;
